import type { DetectionBox, Rect } from "@aimosaic/core";
import { dedupeBoxes, TEXT_CATEGORIES } from "@aimosaic/core";

interface AiNoValidDetectionInput {
  finalCount: number;
  vlmTileCount: number;
  vlmFallback: boolean;
}

export function mergeAiVisionBoxes(
  vlmBoxes: DetectionBox[],
  faceBoxes: DetectionBox[],
  safetyNetBoxes: DetectionBox[] = []
): DetectionBox[] {
  const localTextBoxes = safetyNetBoxes.filter(isLocalTextBox);
  const filteredVlmBoxes = vlmBoxes
    .map((box) => (box.category === "face" && box.source === "local-vlm" ? tightenVlmFaceBox(box) : box))
    .filter((box) => !shouldDropVlmTextBox(box, localTextBoxes));
  const allBoxes = [...safetyNetBoxes, ...filteredVlmBoxes, ...faceBoxes];
  const textAndOtherBoxes = dedupeBoxes(allBoxes.filter((box) => box.category !== "face"));
  const mergedFaceBoxes = mergeFaceBoxes(allBoxes.filter((box) => box.category === "face"));
  return [...textAndOtherBoxes, ...mergedFaceBoxes].sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);
}

export function shouldShowAiNoValidDetections({
  finalCount,
  vlmTileCount,
  vlmFallback
}: AiNoValidDetectionInput): boolean {
  return finalCount === 0 && vlmTileCount > 0 && !vlmFallback;
}

function mergeFaceBoxes(faceBoxes: DetectionBox[]): DetectionBox[] {
  const merged: DetectionBox[] = [];
  const localFaces = faceBoxes
    .filter((box) => box.source === "local-face")
    .sort((a, b) => a.bbox.x - b.bbox.x || a.bbox.y - b.bbox.y);
  for (const face of localFaces) {
    addOrReplaceFace(merged, face);
  }

  const vlmFaces = faceBoxes
    .filter((box) => box.source !== "local-face")
    .sort((a, b) => a.bbox.x - b.bbox.x || a.bbox.y - b.bbox.y);

  for (const face of vlmFaces) {
    if (
      !localFaces.some((candidate) => isNearTrustedLocalFace(candidate.bbox, face.bbox)) &&
      !merged.some((candidate) => isSameFace(candidate.bbox, face.bbox))
    ) {
      merged.push(face);
    }
  }

  return merged.sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);
}

function addOrReplaceFace(merged: DetectionBox[], face: DetectionBox): void {
  const duplicateIndex = merged.findIndex((candidate) => isSameFace(candidate.bbox, face.bbox));
  if (duplicateIndex === -1) {
    merged.push(face);
    return;
  }
  if (isPreferredFace(face, merged[duplicateIndex])) {
    merged[duplicateIndex] = face;
  }
}

function isPreferredFace(candidate: DetectionBox, current: DetectionBox): boolean {
  if (candidate.source === "local-face" && current.source !== "local-face") return true;
  if (current.source === "local-face" && candidate.source !== "local-face") return false;
  if (candidate.confidence >= current.confidence + 0.05) return true;
  if (current.confidence >= candidate.confidence + 0.05) return false;
  return area(candidate.bbox) < area(current.bbox);
}

function tightenVlmFaceBox(box: DetectionBox): DetectionBox {
  const width = box.bbox.width;
  const height = box.bbox.height;
  if (height <= width * 1.2) return box;
  return {
    ...box,
    bbox: {
      ...box.bbox,
      height: Math.max(width, height * 0.72)
    }
  };
}

function shouldDropVlmTextBox(box: DetectionBox, localTextBoxes: DetectionBox[]): boolean {
  if (box.source !== "local-vlm" || !TEXT_CATEGORIES.has(box.category)) return false;
  if (!isValidVlmTextCandidate(box)) return true;
  if (isOversizedTextBox(box.bbox)) return true;
  return localTextBoxes.some((localBox) => isBetterLocalTextBox(box, localBox));
}

function isLocalTextBox(box: DetectionBox): boolean {
  return box.source !== "local-vlm" && TEXT_CATEGORIES.has(box.category);
}

function isBetterLocalTextBox(vlmBox: DetectionBox, localBox: DetectionBox): boolean {
  if (vlmBox.category !== localBox.category) return false;
  const vlmText = normalizeTextValue(vlmBox.text);
  const localText = normalizeTextValue(localBox.text);
  if (vlmText && vlmText === localText) return true;
  if (vlmBox.category !== "ip" && vlmBox.category !== "phone" && vlmBox.category !== "resident-id") return false;
  return rectsOverlapOnLine(vlmBox.bbox, localBox.bbox);
}

function isOversizedTextBox(box: Rect): boolean {
  return box.height > Math.max(96, box.width * 0.35);
}

function isValidVlmTextCandidate(box: DetectionBox): boolean {
  const text = normalizeTextForValidation(box.text);
  if (!text) return false;
  if (looksLikeExplanatoryOrHeading(text, box.reason)) return false;

  if (box.category === "email") return isEmailLike(text);
  if (box.category === "ip") return isIpv4Like(text);
  if (box.category === "url") return isUrlLike(text) && !looksLikeFileReferenceText(text);
  if (box.category === "phone") return isPhoneLike(text);
  if (box.category === "resident-id") return isResidentIdLike(text);
  if (box.category === "token" || box.category === "secret") return isTokenOrSecretLike(text);
  return true;
}

function normalizeTextForValidation(text: string | undefined): string {
  return (text ?? "").trim().replace(/[\u2010-\u2015\u2212]/g, "-");
}

function looksLikeExplanatoryOrHeading(text: string, reason: string | undefined): boolean {
  const normalized = text.toLowerCase();
  const normalizedReason = reason?.toLowerCase() ?? "";
  if (/(?:아닙니다|아님|not\s+(?:sensitive|secret|token|url)|not\s+a\s+(?:secret|token|url)|section title|heading|file path|filename|version|oid|checksum)/i.test(`${normalized} ${normalizedReason}`)) {
    return true;
  }
  if (/^(?:\d+\.\s*)?(?:토큰|시크릿|키|비밀번호|token|secret|key|password)(?:\s*[,/·]\s*(?:토큰|시크릿|키|비밀번호|token|secret|key|password))*$/i.test(text.trim())) {
    return true;
  }
  return false;
}

function isEmailLike(text: string): boolean {
  return /^[^\s@]+@[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?)+$/i.test(text);
}

function isIpv4Like(text: string): boolean {
  const parts = text.split(".");
  return parts.length === 4 && parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;
    const value = Number(part);
    return value >= 0 && value <= 255;
  });
}

function isUrlLike(text: string): boolean {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(text)) return true;
  if (/^(?:localhost|\d{1,3}(?:\.\d{1,3}){3})(?::\d+)?(?:[/?#][^\s]*)?$/i.test(text)) return true;
  return /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?)+(?:\:\d+)?(?:[/?#][^\s]*)?$/i.test(text);
}

function looksLikeFileReferenceText(text: string): boolean {
  if (/^\/(?:Users|Volumes|private|tmp|var)\//i.test(text)) return true;
  if (/^[A-Z]:\\/i.test(text)) return true;
  return /\.(?:txt|md|markdown|png|jpe?g|webp|gif|pdf|docx?|xlsx?|csv|log|json|ya?ml)$/i.test(text);
}

function isPhoneLike(text: string): boolean {
  return /^(?:\+82[-\s]?0?1\d[-\s]?\d{3,4}[-\s]?\d{4}|0\d{1,2}[-\s]?\d{3,4}[-\s]?\d{4})$/.test(text);
}

function isResidentIdLike(text: string): boolean {
  if (/^\d{6}[-\s]?[1-8]\d{6}$/.test(text)) return true;
  const date = /^(20\d{2}|19\d{2})[-/.](0?[1-9]|1[0-2])[-/.](0?[1-9]|[12]\d|3[01])$/.exec(text);
  if (!date) return false;
  return Number(date[1]) < 2020;
}

function isTokenOrSecretLike(text: string): boolean {
  const normalized = text.trim();
  if (normalized.length < 8) return false;
  if (/^(?:free|type|none|null|true|false)$/i.test(normalized)) return false;
  if (/^\d+\s+(?:years?|months?|days?)\s+ago$/i.test(normalized)) return false;
  if (/^0x[0-9a-f]{4,16}$/i.test(normalized)) return false;
  if (/\b(?:policy|generated|years?\s+ago)\b/i.test(normalized)) return false;
  if (/^eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{3,}\.[A-Za-z0-9_-]{3,}$/.test(normalized)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalized)) return true;
  if (/\b(?:sk|pk|rk|ghp|gho|ghu|glpat)[-_][A-Za-z0-9_-]{10,}\b/.test(normalized)) return true;
  if (/\b[A-Za-z][A-Za-z0-9]*_(?:live|test|prod|dev|secret|key|token|pass|demo)_[A-Za-z0-9_]{8,}\b/.test(normalized)) return true;
  if (!/[A-Za-z]/.test(normalized)) return false;
  return /[0-9]/.test(normalized) || /[-_.=+/]/.test(normalized);
}

function isSameFace(a: Rect, b: Rect): boolean {
  const aCenter = center(a);
  const bCenter = center(b);
  const maxWidth = Math.max(a.width, b.width);
  const maxHeight = Math.max(a.height, b.height);
  return (
    iou(a, b) >= 0.35 ||
    (Math.abs(aCenter.x - bCenter.x) <= maxWidth * 0.3 &&
      Math.abs(aCenter.y - bCenter.y) <= maxHeight * 0.42)
  );
}

function isNearTrustedLocalFace(local: Rect, candidate: Rect): boolean {
  const candidateCenter = center(candidate);
  const expanded = expandRect(local, 0.7);
  return (
    candidateCenter.x >= expanded.x &&
    candidateCenter.x <= expanded.x + expanded.width &&
    candidateCenter.y >= expanded.y &&
    candidateCenter.y <= expanded.y + expanded.height
  );
}

function expandRect(box: Rect, scale: number): Rect {
  const extraWidth = box.width * scale;
  const extraHeight = box.height * scale;
  return {
    x: box.x - extraWidth / 2,
    y: box.y - extraHeight / 2,
    width: box.width + extraWidth,
    height: box.height + extraHeight
  };
}

function rectsOverlapOnLine(a: Rect, b: Rect): boolean {
  const verticalOverlap = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  if (verticalOverlap <= 0) return false;
  const horizontalOverlap = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  return horizontalOverlap > 0 && verticalOverlap / Math.min(a.height, b.height) >= 0.35;
}

function normalizeTextValue(text: string | undefined): string {
  return (text ?? "").trim().toLowerCase().replace(/\s+/g, "");
}

function center(box: Rect): { x: number; y: number } {
  return {
    x: box.x + box.width / 2,
    y: box.y + box.height / 2
  };
}

function iou(a: Rect, b: Rect): number {
  const left = Math.max(a.x, b.x);
  const top = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  const intersection = Math.max(0, right - left) * Math.max(0, bottom - top);
  const union = area(a) + area(b) - intersection;
  return union <= 0 ? 0 : intersection / union;
}

function area(box: Rect): number {
  return Math.max(0, box.width) * Math.max(0, box.height);
}
